/**
 * FLOOR FRAMING round (docs/MODELER.md) — the wall-framing curated-data pattern
 * applied to floor joists. Pure data + lookups, no geometry — bim-geometry's floor
 * framing generator (categories/floor/framing.ts) and the floor schema's choice list
 * both read from HERE so the curated set has exactly one home.
 *
 * Representation-grade only, the standing E2/wall-framing guard: no blocking,
 * bridging, hangers, or engineering, ever.
 *
 * JOIST DEPTH derives from the TYPE's core definition now (FLOOR CORE DEFINITION
 * round — floorCore.ts: the declared joist size sets the core layer's thickness,
 * which IS the joist depth). Spacing is an INSTANCE param (the wall studSpacingIn
 * precedent). Joist WIDTH follows the family: sawn 2× stock, TJI flange width.
 *
 * Default-on for any wood-core floor, no per-instance toggle (the wall-framing
 * round's model, not the roof's per-element study): visibility is controlled ONLY
 * by the live 3D viewport's global Framing setting.
 */
import type { ResolvedElement } from './params'

/** joist module choices, inches on center (FLOOR CORE DEFINITION round, 2026-08-18:
 *  Matt's curated list — 19.2 dropped for 18/20) */
export const JOIST_SPACINGS = [12, 16, 18, 20, 24] as const

/** 2× stock actual width — joist plan width along the run, same convention as a stud */
export const JOIST_WIDTH_IN = 1.5

/**
 * Does this floor's assembly have a WOOD structural core? Non-wood assemblies
 * (concrete slab-on-grade, etc.) have no framing derivation at all — the signal is
 * the resolved core-layer MATERIAL (ResolvedElement.coreMaterial), mirroring
 * wallFraming.ts#isWoodCoreWall exactly.
 */
export function isWoodCoreFloor(el: ResolvedElement): boolean {
  if (el.category !== 'floor' || !el.assembly) return false
  const m = el.coreMaterial
  if (!m) return false
  if (m.category === 'Wood') return true
  // FLOOR CORE DEFINITION round: TJI joists frame too (engineered wood — the
  // canonical 'TJI Joist' material carries category 'Wood', the regex covers
  // any pre-family record named by hand)
  return /\b(wood|stud|joist|tji|timber|lumber)\b/i.test(m.name)
}

/**
 * SEAM BEAM SIZES (2026-10-08, Matt: "add beam sizing … joist sizes are set by the
 * slab type"): the curated sizes a framing line's beam can take. The `joist*` rows
 * are built from the floor's own joists (plies of the joist width, joist depth) —
 * `joist2` is the default. Actual (dressed) dimensions, inches. A flush beam keeps
 * its top flush with the joists and hangs below them when deeper; a dropped beam
 * sits under them.
 */
export interface SeamBeamSize {
  value: string
  label: string
  group: 'Joist plies' | 'Sawn' | 'LVL' | 'Glulam'
  /** null = plies × the floor's joist width / the floor's joist depth */
  widthIn: number | null
  depthIn: number | null
  plies?: number
}
const lvl = (plies: number, d: number, dl: string): SeamBeamSize =>
  ({ value: `lvl${plies}-${d}`, label: `${plies}-ply 1¾″ × ${dl} LVL`, group: 'LVL', widthIn: 1.75 * plies, depthIn: d })
const glb = (w: number, wl: string, d: number, dl: string): SeamBeamSize =>
  ({ value: `glb${w}-${d}`, label: `${wl} × ${dl} Glulam`, group: 'Glulam', widthIn: w, depthIn: d })
export const SEAM_BEAM_SIZES: SeamBeamSize[] = [
  { value: 'joist2', label: '2-ply joist (default)', group: 'Joist plies', widthIn: null, depthIn: null, plies: 2 },
  { value: 'joist3', label: '3-ply joist', group: 'Joist plies', widthIn: null, depthIn: null, plies: 3 },
  { value: 'joist4', label: '4-ply joist', group: 'Joist plies', widthIn: null, depthIn: null, plies: 4 },
  { value: '4x8', label: '4×8', group: 'Sawn', widthIn: 3.5, depthIn: 7.25 },
  { value: '4x10', label: '4×10', group: 'Sawn', widthIn: 3.5, depthIn: 9.25 },
  { value: '4x12', label: '4×12', group: 'Sawn', widthIn: 3.5, depthIn: 11.25 },
  { value: '6x8', label: '6×8', group: 'Sawn', widthIn: 5.5, depthIn: 7.5 },
  { value: '6x10', label: '6×10', group: 'Sawn', widthIn: 5.5, depthIn: 9.5 },
  { value: '6x12', label: '6×12', group: 'Sawn', widthIn: 5.5, depthIn: 11.5 },
  { value: '6x14', label: '6×14', group: 'Sawn', widthIn: 5.5, depthIn: 13.5 },
  lvl(2, 9.25, '9¼″'), lvl(2, 11.25, '11¼″'), lvl(2, 11.875, '11⅞″'), lvl(2, 14, '14″'), lvl(2, 16, '16″'), lvl(2, 18, '18″'),
  lvl(3, 11.875, '11⅞″'), lvl(3, 14, '14″'), lvl(3, 16, '16″'), lvl(3, 18, '18″'),
  glb(3.125, '3⅛″', 9, '9″'), glb(3.125, '3⅛″', 10.5, '10½″'), glb(3.125, '3⅛″', 12, '12″'), glb(3.125, '3⅛″', 13.5, '13½″'),
  glb(5.125, '5⅛″', 9, '9″'), glb(5.125, '5⅛″', 10.5, '10½″'), glb(5.125, '5⅛″', 12, '12″'), glb(5.125, '5⅛″', 13.5, '13½″'),
  glb(5.125, '5⅛″', 15, '15″'), glb(5.125, '5⅛″', 16.5, '16½″'), glb(5.125, '5⅛″', 18, '18″'),
]
export function seamBeamSize(value: unknown): SeamBeamSize | null {
  return SEAM_BEAM_SIZES.find((s) => s.value === value) ?? null
}
